import Button from "@components/ui/button";
import DialogBase from "@components/ui/dialog-base";
import { ScrollArea } from "@components/ui/scroll-area";
import { useAppDispatch, useAppSelector } from "@store";
import { closeDialog } from "@store/dialogs";
import { setVersionDataValue } from "@store/story";
import {
  getRepeatableTree,
  getVersionData,
  isVersionInEditor,
} from "@store/story/selectors";
import { updateRepeatableDefinition } from "@store/story/thunks/repeatable.thunk";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { RepeatableDefinitionsProvider } from "@/dialogs/share-dialog/components/versions/create-or-edit-version/variables/repeatable/repeatable-definitions-context";
import { useFastVarDefinitions } from "@/dialogs/share-dialog/components/versions/create-or-edit-version/variables/repeatable/use-fast-var-definitions";
import { RepeatableGroupCard } from "@/dialogs/share-dialog/components/versions/create-or-edit-version/variables/repeatable/repeatable-group-card";
import { findTreeNode, rootKeyOf } from "../scope";

const ID = "repeatable-rows";

export interface RepeatableRowsDialogProps {
  /** Group to edit. May be a NESTED key — the root's rows are edited either way. */
  repeatableKey: string;
  /** Nested group to scroll to and highlight inside row 1 of each level. */
  focusKey?: string;
}

/**
 * The rows pop-up: the editor renders a repeatable group bound to row 1 only,
 * so every row is edited here instead. Hosts the share dialog's
 * `RepeatableGroupCard` unchanged, on a form of its own.
 *
 * Rows live in the version's `data` when a version is open in the editor, and
 * on the group definition's `predefined` otherwise — same split as the share
 * dialog's variable form.
 */
export const RepeatableRowsDialog = ({
  repeatableKey,
  focusKey,
}: RepeatableRowsDialogProps) => {
  const dispatch = useAppDispatch();
  const roots = useAppSelector(getRepeatableTree);
  const isVersion = useAppSelector(isVersionInEditor);
  const versionData = useAppSelector(getVersionData) as
    | Record<string, unknown>
    | undefined;

  // A nested key edits the OUTERMOST group: nested rows are stored inside the
  // parent row that owns them, so only the root has a standalone row list.
  const rootKey = rootKeyOf(roots, repeatableKey) ?? repeatableKey;
  const node = findTreeNode(roots, rootKey);

  const fromVersion = isVersion ? versionData?.[rootKey] : undefined;
  const initialRows = Array.isArray(fromVersion)
    ? (fromVersion as Record<string, unknown>[])
    : (node?.repeatable.predefined ?? []);

  // `useForm` reads `defaultValues` once, on mount — deliberately: the store
  // keeps changing under the pop-up (autosave, collab) and a reset would
  // discard whatever the user is typing.
  const form = useForm<Record<string, unknown>>({
    defaultValues: { [rootKey]: initialRows },
  });
  // Org variable definitions for the row cells (select options, AI prompts,
  // default values) — the dialog has no share-dialog variables hook to lean on.
  const definitions = useFastVarDefinitions();

  const close = () => dispatch(closeDialog(ID));

  // The group can vanish while the pop-up is open (undo, collab, a deleted
  // layer), and it may never have existed for a stale key.
  useEffect(() => {
    if (!node) dispatch(closeDialog(ID));
  }, [node, dispatch]);

  if (!node) return null;

  const save = form.handleSubmit((values) => {
    const value = values[rootKey];
    const rows = Array.isArray(value)
      ? (value as Record<string, unknown>[])
      : [];
    if (isVersion) dispatch(setVersionDataValue({ key: rootKey, value: rows }));
    else
      dispatch(updateRepeatableDefinition({ key: rootKey, predefined: rows }));
    close();
  });

  return (
    // Same shell as the "Make optional" dialog (make-optional-dialog.tsx):
    // DialogBase — soft 1px outline, large shadow, 2xl radius, borderless
    // header — so the two entity dialogs read as one family.
    <DialogBase className="tw-flex tw-max-h-[85vh] tw-w-[536px] tw-flex-col tw-overflow-hidden tw-rounded-2xl">
      <DialogBase.Header
        title={node.repeatable.name}
        onClose={close}
        className="tw-h-auto tw-shrink-0 tw-border-none tw-pt-6 [&_span]:tw-text-lg"
      />
      <div className="tw-shrink-0 tw-px-6 tw-pb-3 tw-pt-8">
        <p className="tw-m-0 tw-font-sans tw-text-sm tw-text-blue-700">
          {isVersion
            ? "Rows for this version. Row 1 is what the editor shows."
            : "Default rows, used until a sender fills them in. Row 1 is what the editor shows."}
        </p>
      </div>
      <ScrollArea className="tw-min-h-0 tw-flex-1" maxHeight={327}>
        <div className="tw-px-6 tw-pb-2 tw-pt-5">
          <RepeatableDefinitionsProvider definitions={definitions} form={form}>
            <RepeatableGroupCard
              // Spread: `children` is a plain data prop here, not JSX content.
              {...{
                repeatable: node.repeatable,
                form,
                children: node.children,
                editorRowIndex: 0,
                focusKey: focusKey === rootKey ? undefined : focusKey,
              }}
            />
          </RepeatableDefinitionsProvider>
        </div>
      </ScrollArea>
      <div className="tw-flex tw-shrink-0 tw-justify-end tw-gap-3 tw-px-6 tw-pb-6 tw-pt-5">
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button onClick={save}>Save rows</Button>
      </div>
    </DialogBase>
  );
};

RepeatableRowsDialog.id = ID;
